import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { DateTime } from 'luxon'
import styled from 'styled-components'
import { v4 as uuidv4 } from 'uuid'
import { DEFAULT_FOLDER_ID, DONE_FOLDER_ID, TRASH_FOLDER_ID } from '../../constants'
import {
    useDeferredInitialLoad,
    useIsMobile,
    useKeyboardShortcut,
    useMobileDetailRouteFallback,
    useNavigateToTask,
    useShowDetailsPane,
} from '../../hooks'
import useItemSelectionController from '../../hooks/useItemSelectionController'
import { useGetFolders } from '../../services/api/folders.hooks'
import Log from '../../services/api/log'
import { useGetMeetingPreparationTasks } from '../../services/api/meeting-preparation-tasks.hooks'
import { useCreateTask, useFetchExternalTasks, useReorderTask } from '../../services/api/tasks.hooks'
import { useGetTasksV4 } from '../../services/api/tasks.hooks'
import { icons } from '../../styles/images'
import { DAILY_HOME_AUTOMATIC_REDIRECT_STATE } from '../../utils/dailyHome'
import SortAndFilterSelectors from '../../utils/sortAndFilter/SortAndFilterSelectors'
import { canReorderTaskList } from '../../utils/sortAndFilter/canReorderTaskList'
import sortAndFilterItems from '../../utils/sortAndFilter/sortAndFilterItems'
import { TASK_SORT_AND_FILTER_CONFIG } from '../../utils/sortAndFilter/tasks.config'
import useSortAndFilterSettings from '../../utils/sortAndFilter/useSortAndFilterSettings'
import { DropItem, DropType, TTaskV4 } from '../../utils/types'
import ActionsContainer from '../atoms/ActionsContainer'
import ReorderDropContainer from '../atoms/ReorderDropContainer'
import Spinner from '../atoms/Spinner'
import EmptyDetails from '../details/EmptyDetails'
import TaskDetails from '../details/TaskDetails'
import CreateNewItemInput from '../molecules/CreateNewItemInput'
import { Header } from '../molecules/Header'
import Task from '../molecules/Task'
import ScrollableListTemplate from '../templates/ScrollableListTemplate'

const TasksContainer = styled.div`
    display: flex;
    flex-direction: column;
`
const BottomDropArea = styled.div`
    height: 100px;
`

type TaskSectionLocationState = {
    focusCreateInputOnLaunch?: boolean
}

const TaskSectionView = () => {
    const [shouldScrollToTask, setShouldScrollToTask] = useState(false)
    const sectionScrollingRef = useRef<HTMLDivElement | null>(null)
    const sectionViewRef = useRef<HTMLDivElement>(null)

    const isMobile = useIsMobile()
    const shouldLoadSecondaryQueries = useDeferredInitialLoad()
    const showDetailsPane = useShowDetailsPane()
    const { data: meetingPreparationTasks, isLoading: isLoadingMeetingPreparationTasks } =
        useGetMeetingPreparationTasks()
    const { data: allTasks, isLoading: isLoadingTasks } = useGetTasksV4()
    const { data: folders, isLoading: isLoadingFolders } = useGetFolders()
    const { mutate: createTask } = useCreateTask()
    const { mutate: reorderTask } = useReorderTask()
    useFetchExternalTasks(shouldLoadSecondaryQueries)
    const canValidateTaskRoute = !isLoadingFolders && !isLoadingTasks && !isLoadingMeetingPreparationTasks

    const navigate = useNavigate()
    const location = useLocation()
    const params = useParams()
    const navigateToTask = useNavigateToTask()
    const locationState = location.state as TaskSectionLocationState | null
    const shouldFocusCreateInputOnLaunch = isMobile && !params.task && Boolean(locationState?.focusCreateInputOnLaunch)
    // A launch can arrive while the list is still a spinner (e.g. the floating add button tapped before
    // sort settings load), so hold the request until the input mounts and reads it as autoFocus.
    const shouldAutoFocusCreateInput = useRef(shouldFocusCreateInputOnLaunch)
    if (shouldFocusCreateInputOnLaunch) shouldAutoFocusCreateInput.current = true

    useEffect(() => {
        if (!shouldFocusCreateInputOnLaunch) return
        navigate(location.pathname, {
            replace: true,
            state: { ...(locationState ?? {}), focusCreateInputOnLaunch: false },
        })
    }, [location.pathname, locationState, navigate, shouldFocusCreateInputOnLaunch])

    const folder = useMemo(() => folders?.find(({ id }) => id === params.section), [folders, params.section])
    const folderTasks = useMemo(() => {
        if (!folder) return []
        if (folder.id === DONE_FOLDER_ID) {
            return [
                ...(meetingPreparationTasks?.filter((t) => t.is_done && !t.is_deleted) || []),
                ...(allTasks?.filter((t) => t.is_done && !t.is_deleted && !t.id_parent) || []),
            ].sort((a, b) => +DateTime.fromISO(b.completed_at) - +DateTime.fromISO(a.completed_at))
        } else if (folder.id === TRASH_FOLDER_ID) {
            return [
                ...(meetingPreparationTasks?.filter((t) => t.is_deleted) || []),
                ...(allTasks?.filter((t) => t.is_deleted) || []),
            ].sort((a, b) => +DateTime.fromISO(b.deleted_at) - +DateTime.fromISO(a.deleted_at))
        }
        return allTasks?.filter((t) => t.id_folder === folder.id && !t.is_done && !t.is_deleted) || []
    }, [allTasks, folder, meetingPreparationTasks])

    const sortAndFilterSettings = useSortAndFilterSettings<TTaskV4>(
        TASK_SORT_AND_FILTER_CONFIG,
        folder?.id,
        '_main',
        shouldLoadSecondaryQueries
    )
    const { selectedSort, selectedSortDirection, isLoading: areSettingsLoading } = sortAndFilterSettings
    const sortedTasks = useMemo(() => {
        if (folder && (folder.is_done || folder.is_trash)) return folderTasks
        if (!folder || areSettingsLoading) return []

        return sortAndFilterItems<TTaskV4>({
            items: folderTasks,
            sort: selectedSort,
            sortDirection: selectedSortDirection,
            tieBreakerField: TASK_SORT_AND_FILTER_CONFIG.tieBreakerField,
        })
    }, [folder, folderTasks, selectedSort, selectedSortDirection, areSettingsLoading])

    const task = useMemo(() => {
        const subtask = allTasks?.find(({ id }) => id === params.subtaskId)
        if (subtask) return subtask
        const task = allTasks?.find(({ id }) => id === params.task)
        if (task) return task
        const meetingTask = meetingPreparationTasks?.find(({ id }) => id === params.task)
        return meetingTask
    }, [allTasks, meetingPreparationTasks, params.task, params.subtaskId])
    const shouldShowMobileDetailSpinner = useMobileDetailRouteFallback({
        isMobile,
        detailId: params.task,
        canValidate: canValidateTaskRoute && Boolean(folder),
        hasSelectedDetail: Boolean(task && folder),
        listPath: folder ? `/tasks/${folder.id}/` : '/tasks',
    })

    const [taskIndex, setTaskIndex] = useState(0)

    useEffect(() => {
        if (task) {
            const index = sortedTasks.findIndex(({ id }) => id === task.id)
            setTaskIndex(index === -1 ? 0 : index)
        }
    }, [params.task, params.section, sortedTasks, task])

    const selectTask = useCallback(
        (task: TTaskV4) => {
            setShouldScrollToTask(true)
            if (folder) {
                navigate(`/tasks/${folder.id}/${task.id}`, { replace: true })
                Log(`task_select_${task.id}`)
            }
        },
        [folder]
    )
    const handleReorderTask = useCallback(
        (item: DropItem, dropIndex: number) => {
            if (!folder) return
            reorderTask(
                {
                    id: item.id,
                    orderingId: dropIndex,
                    dropSectionId: folder.id,
                },
                item.task?.optimisticId
            )
        },
        [folder]
    )

    // Same arithmetic as the moveTaskUp/moveTaskDown shortcuts below: id_ordering is 1-based, and
    // moving down has to clear the slot of the task it swaps with.
    const canReorderTasks = canReorderTaskList(selectedSort.id, folder)
    const moveTask = useCallback(
        (taskToMove: TTaskV4, direction: 'up' | 'down', folderId: string) =>
            reorderTask(
                {
                    id: taskToMove.id,
                    orderingId: direction === 'up' ? taskToMove.id_ordering - 1 : taskToMove.id_ordering + 2,
                    dropSectionId: folderId,
                },
                taskToMove.optimisticId
            ),
        [reorderTask]
    )

    // deal with invalid routes
    useEffect(() => {
        if (folders && folders.length > 0 && (!folder || !task)) {
            const firstFolderId = folders[0].id
            if (!folder) {
                navigate(`/tasks/${firstFolderId}/`, {
                    replace: true,
                    state: DAILY_HOME_AUTOMATIC_REDIRECT_STATE,
                })
            } else if (isMobile) {
                if (params.task) {
                    if (!canValidateTaskRoute) return
                    navigate(`/tasks/${folder.id}/`, {
                        replace: true,
                        state: DAILY_HOME_AUTOMATIC_REDIRECT_STATE,
                    })
                }
            } else if (!task && sortedTasks.length > taskIndex) {
                navigate(`/tasks/${folder.id}/${sortedTasks[taskIndex].id}`, { replace: true })
            } else if (!task && sortedTasks.length === taskIndex && taskIndex > 0) {
                navigate(`/tasks/${folder.id}/${sortedTasks[taskIndex - 1].id}`, { replace: true })
            } else if (!task && sortedTasks.length > 0) {
                navigate(`/tasks/${folder.id}/${sortedTasks[0].id}`, { replace: true })
            }
        }
    }, [canValidateTaskRoute, folders, isMobile, params.section, params.task, sortedTasks])

    useItemSelectionController(sortedTasks, selectTask)

    useKeyboardShortcut(
        'moveTaskDown',
        useCallback(() => {
            if (!task || !folder || taskIndex === sortedTasks.length - 1) return
            reorderTask({
                id: task.id,
                orderingId: task.id_ordering + 2,
                dropSectionId: folder.id,
            })
        }, [task, folder, sortedTasks, taskIndex, sortedTasks]),
        !canReorderTasks
    )
    useKeyboardShortcut(
        'moveTaskUp',
        useCallback(() => {
            if (!task || !folder || taskIndex === 0) return
            reorderTask({
                id: task.id,
                orderingId: task.id_ordering - 1,
                dropSectionId: folder.id,
            })
        }, [task, folder, sortedTasks, taskIndex]),
        !canReorderTasks
    )

    const selectTaskAfterCompletion = useCallback(
        (taskId: string) => {
            if (!folders) return
            if (params.task !== taskId) return
            const folderIndex = folders.findIndex(({ id }) => id === params.section)
            const taskIndex = sortedTasks.findIndex(({ id }) => id === taskId)
            if (folderIndex == null || taskIndex == null) return

            if (folders.length === 0 || sortedTasks.length === 0) return
            const taskToSelect = sortedTasks[taskIndex === 0 ? taskIndex + 1 : taskIndex - 1]
            if (!taskToSelect) return
            navigateToTask({ taskId: taskToSelect.id })
        },
        [folders, params.task]
    )

    const isListLoading = isLoadingTasks || !folder || (areSettingsLoading && !folder.is_done && !folder.is_trash)
    // Once the input has mounted it has consumed any pending autofocus; don't replay it on a later remount.
    useEffect(() => {
        if (!isListLoading) shouldAutoFocusCreateInput.current = false
    })

    return (
        <>
            <ScrollableListTemplate ref={sectionScrollingRef}>
                {isLoadingTasks || !folder || (areSettingsLoading && !folder.is_done && !folder.is_trash) ? (
                    <Spinner />
                ) : (
                    <>
                        <Header folderName={folder.name} folderId={folder.id} />
                        {!folder.is_done && !folder.is_trash && (
                            <ActionsContainer
                                leftActions={<SortAndFilterSelectors settings={sortAndFilterSettings} />}
                            />
                        )}
                        {!folder.is_done && !folder.is_trash && (
                            <CreateNewItemInput
                                placeholder="Create new task"
                                submitLabel="Add task"
                                shortcutName="createTask"
                                autoFocus={shouldAutoFocusCreateInput.current}
                                isDefaultTaskCaptureInput={folder.id === DEFAULT_FOLDER_ID}
                                onSubmit={(title) =>
                                    createTask({
                                        title: title,
                                        id_folder: folder.id,
                                        optimisticId: uuidv4(),
                                    })
                                }
                            />
                        )}
                        <TasksContainer ref={sectionViewRef}>
                            {sortedTasks.map((task, index) => (
                                <ReorderDropContainer
                                    key={task.id}
                                    index={index}
                                    acceptDropType={DropType.TASK}
                                    onReorder={handleReorderTask}
                                    disabled={!canReorderTasks}
                                >
                                    <Task
                                        task={task}
                                        reorderDisabled={!canReorderTasks}
                                        index={index}
                                        sectionScrollingRef={sectionScrollingRef}
                                        isSelected={task.id === params.task}
                                        link={`/tasks/${params.section}/${task.id}`}
                                        shouldScrollToTask={shouldScrollToTask}
                                        setShouldScrollToTask={setShouldScrollToTask}
                                        onMarkTaskDone={selectTaskAfterCompletion}
                                        reorder={
                                            canReorderTasks
                                                ? {
                                                      moveUp:
                                                          index === 0
                                                              ? undefined
                                                              : () => moveTask(task, 'up', folder.id),
                                                      moveDown:
                                                          index === sortedTasks.length - 1
                                                              ? undefined
                                                              : () => moveTask(task, 'down', folder.id),
                                                  }
                                                : undefined
                                        }
                                    />
                                </ReorderDropContainer>
                            ))}
                        </TasksContainer>
                        <ReorderDropContainer
                            index={sortedTasks.length + 1}
                            acceptDropType={DropType.TASK}
                            onReorder={handleReorderTask}
                            indicatorType="TOP_ONLY"
                            disabled={!canReorderTasks}
                        >
                            <BottomDropArea />
                        </ReorderDropContainer>
                    </>
                )}
            </ScrollableListTemplate>
            {showDetailsPane && (!isMobile || params.task) && (
                <>
                    {task && folder ? (
                        <TaskDetails task={task} />
                    ) : shouldShowMobileDetailSpinner ? (
                        <Spinner />
                    ) : (
                        <EmptyDetails icon={icons.check} text="You have no tasks" />
                    )}
                </>
            )}
        </>
    )
}

export default TaskSectionView
